import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'vfx_token';

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(!!token);

  const applyAuth = useCallback((t) => {
    if (t) {
      localStorage.setItem(TOKEN_KEY, t);
      axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
    } else {
      localStorage.removeItem(TOKEN_KEY);
      delete axios.defaults.headers.common['Authorization'];
    }
    setToken(t || null);
  }, []);

  // Hydrate current user from token
  useEffect(() => {
    let cancelled = false;
    async function boot() {
      if (!token) {
        setLoading(false);
        return;
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      try {
        const res = await axios.get(`${API}/auth/me`);
        if (!cancelled) setUser(res.data.user);
      } catch {
        if (!cancelled) applyAuth(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    boot();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    applyAuth(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const signup = async (name, email, password) => {
    const res = await axios.post(`${API}/auth/signup`, { name, email, password });
    applyAuth(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = () => {
    applyAuth(null);
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, token, loading, login, signup, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export { API };
